import * as Api from '@ViewModels';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { extractArrayFromResponse } from '../../queries/utils';
import type { Category, Scenario, SkillArea } from './types';

const SCENARIOS_QUERY_KEY = 'scenarios';
const SKILL_AREAS_QUERY_KEY = 'skillAreas';
const CATEGORIES_QUERY_KEY = 'categories';

/**
 * Fetch the scenarios available to the current user (role-filtered server-side).
 *
 * Forked from `queries/Scenarios/useScenarios.ts` so the focused-view Spar core stays
 * self-contained; typed against `./types` rather than the legacy voice-agent types.
 */
export function useMyScenarios({ enabled = true }: { enabled?: boolean } = {}): UseQueryResult<Scenario[], Error> {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<{
				value?: Scenario[];
				values?: Scenario[];
			}>(Api.ImpersonationBroker.composeApiUrl({ urlPath: 'aida/scenarios/my-scenarios' }), 'GET');
			return extractArrayFromResponse<Scenario>(response.value);
		},
		queryKey: [SCENARIOS_QUERY_KEY, 'my-scenarios'],
	});
}

/**
 * Fetch the current user's skill areas (role-filtered server-side). Skill areas
 * map a scenario (`scenario.skillAreaId`) up to its category (`skillArea.categoryId`),
 * which is the level the `ScenarioPicker` groups by.
 *
 * Forked from `queries/Scenarios/useSkillAreas.ts` (`useMySkillAreas`) so the
 * focused-view Spar core stays self-contained.
 */
export function useMySkillAreas({ enabled = true }: { enabled?: boolean } = {}): UseQueryResult<SkillArea[], Error> {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<{
				value?: SkillArea[];
				values?: SkillArea[];
			}>(Api.ImpersonationBroker.composeApiUrl({ urlPath: 'aida/skillareas/skillareas' }), 'GET');
			return extractArrayFromResponse<SkillArea>(response.value);
		},
		queryKey: [SKILL_AREAS_QUERY_KEY, 'my-skillareas'],
	});
}

/**
 * Fetch the current user's scenario categories (role-filtered server-side).
 * Categories are the top of the Category → Skill Area → Scenario hierarchy and
 * are what the `ScenarioPicker` groups scenarios under (skill areas are ~1:1
 * with scenarios, so they make poor group headers).
 *
 * Forked from `queries/Scenarios/useCategories.ts` (`useMyCategories`).
 */
export function useMyCategories({ enabled = true }: { enabled?: boolean } = {}): UseQueryResult<Category[], Error> {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<{
				value?: Category[];
				values?: Category[];
			}>(Api.ImpersonationBroker.composeApiUrl({ urlPath: 'aida/categories/categories' }), 'GET');
			return extractArrayFromResponse<Category>(response.value);
		},
		queryKey: [CATEGORIES_QUERY_KEY, 'my-categories'],
	});
}
